import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import Cookies from 'js-cookie';
import { AppRoutes } from '@/routes';
import { Navbar } from '@components/layout/Navbar';
import { useTenant } from '@providers/TenantProvider';
import { useAppStore } from '@stores/appStore';
import { apiClient, AUTH_COOKIES, storeAccessToken, isAuthenticated } from '@lib/api';

/**
 * Main App component
 */
function App() {
  const location = useLocation();
  const { config } = useTenant();
  const setUserEmail = useAppStore((state) => state.setUserEmail);
  
  const [isInitialized, setIsInitialized] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  
  const isSignedOutPage = location.pathname === '/signed-out';

  // Handle OAuth login flow
  useEffect(() => {
    if (isSignedOutPage) {
      setIsLoading(false);
      setIsInitialized(true);
      return;
    }

    async function initAuth() {
      // Check if already authenticated
      if (isAuthenticated()) {
        setIsInitialized(true);
        setIsLoading(false);
        return;
      }

      // Check for sessionId from OAuth callback
      const params = new URLSearchParams(window.location.search);
      const sessionId = params.get('sessionId');

      if (!sessionId) {
        // No session - redirect to login
        initiateLogin();
        return;
      }

      try {
        // Fetch tokens from OAuth server
        const oauthServerUrl = import.meta.env.VITE_OAUTH_SERVER_URL;
        const redirectPath = import.meta.env.VITE_OAUTH_CLIENT_REDIRECT_PATH;
        const url = `${oauthServerUrl}${redirectPath}/sessions/${sessionId}`;
        
        const response = await fetch(url);
        const data = await response.json();

        if (data.accessToken) {
          storeAccessToken(
            data.accessToken,
            data.refreshToken,
            'Bearer'
          );
          
          // Remove sessionId from URL
          const newUrl = new URL(window.location.href);
          newUrl.searchParams.delete('sessionId');
          window.history.replaceState({}, '', newUrl.toString());
          
          setIsInitialized(true);
        } else {
          initiateLogin();
        }
      } catch (error) {
        console.error('Auth initialization failed:', error);
        initiateLogin();
      } finally {
        setIsLoading(false);
      }
    }

    initAuth();
  }, [isSignedOutPage]);

  // Fetch user preferences when authenticated
  useEffect(() => {
    if (!isInitialized || isSignedOutPage) return;

    async function fetchUserPreferences() {
      try {
        const response = await apiClient.get('/user-preference');
        setUserEmail(response.data.email);
      } catch (error) {
        console.error('Failed to fetch user preferences:', error);
      }
    }

    fetchUserPreferences();
  }, [isInitialized, isSignedOutPage, setUserEmail]);

  // Update document title based on tenant
  useEffect(() => {
    document.title = `Wint - ${config.branding.companyName} Dashboard`;
  }, [config.branding.companyName]);

  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent mx-auto mb-4" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background font-sans">
      <Navbar isSignedOutPage={isSignedOutPage} />
      <main className="flex-1">
        <AppRoutes />
      </main>
    </div>
  );
}

/**
 * Initiate OAuth login flow
 */
function initiateLogin() {
  const clientId = import.meta.env.VITE_OAUTH_CLIENT_ID;
  const oauthServerUrl = import.meta.env.VITE_OAUTH_SERVER_URL;
  const redirectPath = import.meta.env.VITE_OAUTH_CLIENT_REDIRECT_PATH;
  const loginUrl = import.meta.env.VITE_OAUTH_LOGIN_URL;
  const scope = import.meta.env.VITE_OAUTH_CLIENT_SCOPE;

  // Generate CSRF token
  const csrf = crypto.randomUUID();
  Cookies.set(AUTH_COOKIES.LOGIN_CSRF, csrf, { sameSite: 'Strict' });

  const params = new URLSearchParams({
    response_type: 'code',
    client_id: clientId,
    redirect_uri: `${oauthServerUrl}${redirectPath}?clientId=${clientId}`,
    scope: scope,
    state: csrf,
  });

  window.location.replace(`${loginUrl}?${params.toString()}`);
}

export default App;


